import type { FavoriteView, NavbarConfig } from './types';
import { jsonHeaders } from './apiHelpers';

/** Parse response body as JSON with timeout protection */
async function parseJsonResponse<T>(res: Response, timeoutMs = 5000): Promise<T> {
  const text = await Promise.race([
    res.text(),
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Response body read timed out after ${timeoutMs}ms`)),
        timeoutMs,
      ),
    ),
  ]);
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response (status ${res.status}): ${text.slice(0, 200)}`);
  }
}

/** Make a JSON API request with timeout on both fetch and body read */
async function jsonFetch<T>(url: string, options: RequestInit, timeoutMs = 10000): Promise<T> {
  const controller = new AbortController();
  const fetchTimer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(fetchTimer);

    if (!res.ok) {
      const data = await parseJsonResponse<{ error?: string }>(res).catch(() => ({}));
      throw new Error((data as any).error || `Request failed with status ${res.status}`);
    }

    return await parseJsonResponse<T>(res);
  } catch (e: any) {
    if (e.name === 'AbortError') {
      throw new Error(`Request to ${url} timed out after ${timeoutMs}ms`, { cause: e });
    }
    throw e;
  }
}

export async function createFavoriteView(
  attrs: Omit<FavoriteView, 'id' | 'position'>,
): Promise<{ favoriteView: FavoriteView; favoriteViews: FavoriteView[] }> {
  return jsonFetch('/preferences/favorite_views', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(attrs),
  });
}

export async function updateFavoriteView(
  id: string,
  attrs: Partial<Omit<FavoriteView, 'id' | 'position'>>,
): Promise<{ favoriteView: FavoriteView; favoriteViews: FavoriteView[] }> {
  return jsonFetch(`/preferences/favorite_views/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(attrs),
  });
}

export async function deleteFavoriteView(id: string): Promise<{ favoriteViews: FavoriteView[] }> {
  return jsonFetch(`/preferences/favorite_views/${id}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
}

export async function reorderFavoriteViews(
  orderedIds: string[],
): Promise<{ favoriteViews: FavoriteView[] }> {
  return jsonFetch('/preferences/favorite_views/reorder', {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
}

export async function updateNavbarConfig(
  config: Partial<NavbarConfig>,
): Promise<{ navbarConfig: NavbarConfig }> {
  return jsonFetch('/preferences/navbar_config', {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(config),
  });
}
